import { Dispatch } from "react";
import * as actionTypes from "../actionTypes/Index";
import {
  IActionType,
  IAgentTitle,
  IcreateUserPayload,
  IcreateUserResponse,
  ICustomerConfigResponse,
  IGetCustomerNewDetailResponse,
  IGetQueryParams,
  IGetZoneParams,
} from "../../interfaces/Index";
import {
  fetchCustomerApiCall,
  fetchStoreConfigApiCall,
  fetchStoreTypeApiCall,
  getBulkCustomerDetailsByIdApiCall,
  getCustomerDetailsNewApiCall,
  getCustomerZoneApiCall,
} from "../sources/CustomerSource";
import storeSource, { StoreAddUpdateDto } from "../sources/StoreSource";
import userSource, {
  UserAddUpdateDto,
  UserInfoDto,
} from "../sources/UserSource";

export const fetchCustomerInit = () => ({
  type: actionTypes.GET_CUSTOMERS_INIT,
});

export const fetchCustomerSuccess = (payload: Array<any> | null) => ({
  type: actionTypes.GET_CUSTOMERS_SUCCESS,
  payload,
});

export const fetchCustomerFail = (error: string | object | null) => ({
  type: actionTypes.GET_CUSTOMERS_FAIL,
  error,
});

export const createCustomerInit = () => ({
  type: actionTypes.CREATE_CUSTOMERS_INIT,
});

export const createCustomerSuccess = () => ({
  type: actionTypes.CREATE_CUSTOMERS_SUCCESS,
});

export const createCustomerFail = (error: string | object | null) => ({
  type: actionTypes.CREATE_CUSTOMERS_FAIL,
  error,
});

export const getCustomerDetailsNewInit = () => ({
  type: actionTypes.GET_CUSTOMER_DETAIL_V2_INIT,
});

export const getCustomerDetailsNewSuccess = (payload: Array<any>) => ({
  type: actionTypes.GET_CUSTOMER_DETAIL_V2_SUCCESS,
  payload,
});

export const getCustomerDetailsNewFail = (error: string | object | null) => ({
  type: actionTypes.GET_CUSTOMER_DETAIL_V2_FAIL,
  error,
});

export const clearCustomerDetailsNew = () => ({
  type: actionTypes.CLEAR_DETAIL_V2_DATA,
});

export const getBulkCustomerDetailsInit = () => ({
  type: actionTypes.GET_BULK_CUSTOMER_DETAIL_V2_INIT,
});

export const getBulkCustomerDetailsSuccess = (
  payload: Record<string, IGetCustomerNewDetailResponse>
) => ({
  type: actionTypes.GET_BULK_CUSTOMER_DETAIL_V2_SUCCESS,
  payload,
});

export const getBulkCustomerDetailsFail = (error: string | object | null) => ({
  type: actionTypes.GET_BULK_CUSTOMER_DETAIL_V2_FAIL,
  error,
});

export const clearBulkCustomerDetails = () => ({
  type: actionTypes.CLEAR_BULK_CUSTOMER_DETAIL_V2_DATA,
});

export const updateCustomerInit = () => ({
  type: actionTypes.UPDATE_CUSTOMERS_INIT,
});

export const updateCustomerSuccess = () => ({
  type: actionTypes.UPDATE_CUSTOMERS_SUCCESS,
});

export const updateCustomerFail = (error: string | object | null) => ({
  type: actionTypes.UPDATE_CUSTOMERS_FAIL,
  error,
});

export const getStoreTypeInit = () => ({
  type: actionTypes.GET_STORE_TYPE_INIT,
});

export const getStoreTypeSuccess = (payload: Array<any>) => ({
  type: actionTypes.GET_STORE_TYPE_SUCCESS,
  payload,
});

export const getStoreTypeFail = (error: string | object | null) => ({
  type: actionTypes.GET_STORE_TYPE_FAIL,
  error,
});

export const fetchCustomerConfigInit = () => ({
  type: actionTypes.FETCH_CUSTOMER_CONFIG_INIT,
});

export const fetchCustomerConfigSuccess = (
  payload: ICustomerConfigResponse
) => ({
  type: actionTypes.FETCH_CUSTOMER_CONFIG_SUCCESS,
  payload,
});

export const fetchCustomerConfigFail = (error: string | object | null) => ({
  type: actionTypes.FETCH_CUSTOMER_CONFIG_FAIL,
  error,
});

export const getCustomerZoneInit = () => ({
  type: actionTypes.GET_CUSTOMER_ZONE_INIT,
});

export const getCustomerZoneSuccess = (payload: Array<any>) => ({
  type: actionTypes.GET_CUSTOMER_ZONE_SUCCESS,
  payload,
});

export const getCustomerZoneFail = (error: string | object | null) => ({
  type: actionTypes.GET_CUSTOMER_ZONE_FAIL,
  error,
});

// async actions
export const createCustomer =
  (payload: IcreateUserPayload) => (dispatch: Dispatch<IActionType>) => {
    dispatch(createCustomerInit());
    // using customer id as user id and store id, to keep consistency between shopify and our database
    return (
      userSource
        .createUser(UserAddUpdateDto.fromCreateDto(null, payload))
        .then((user: UserInfoDto) => {
          return storeSource.createStore(
            StoreAddUpdateDto.fromCreateDto(user.id, payload, user.id)
          );
        })
        // })
        .then(() => dispatch(createCustomerSuccess()))
        .catch((e: object | null | string) => dispatch(createCustomerFail(e)))
    );
  };

export const getCustomers =
  (queryParams?: IGetQueryParams) => (dispatch: Dispatch<IActionType>) => {
    dispatch(fetchCustomerInit());
    return fetchCustomerApiCall(queryParams)
      .then((customers: any) => dispatch(fetchCustomerSuccess(customers)))
      .catch((e: object | null | string) => dispatch(fetchCustomerFail(e)));
  };

export const getCustomerDetailsNewAction =
  (id: string) => (dispatch: Dispatch<IActionType>) => {
    dispatch(getCustomerDetailsNewInit());
    return getCustomerDetailsNewApiCall(id)
      .then((meta: any) => dispatch(getCustomerDetailsNewSuccess(meta)))
      .catch((e: object | null | string) =>
        dispatch(getCustomerDetailsNewFail(e))
      );
  };

export const getBulkCustomerDetailsByIdAction =
  (ids: string[]) => (dispatch: Dispatch<IActionType>) => {
    dispatch(getBulkCustomerDetailsInit());
    return getBulkCustomerDetailsByIdApiCall(ids)
      .then((customerDetails: Record<string, IGetCustomerNewDetailResponse>) =>
        dispatch(getBulkCustomerDetailsSuccess(customerDetails))
      )
      .catch((e: object | null | string) => {
        dispatch(getBulkCustomerDetailsFail(e));
      });
  };

export const clearBulkCustomerDetailsAction =
  () => (dispatch: Dispatch<IActionType>) => {
    return dispatch(clearBulkCustomerDetails());
  };

export const clearCustomerDetailsNewAction =
  () => (dispatch: Dispatch<IActionType>) => {
    return dispatch(clearCustomerDetailsNew());
  };

export const updateCustomerAction =
  (payload: IcreateUserResponse) => (dispatch: Dispatch<IActionType>) => {
    dispatch(updateCustomerInit());
    const customerId = String(payload.id);
    const userId = String(payload.userId);
    // return updateCustomerApiCall(payload)
    //   .then(() => {
    // using customer id as user id and store id, to keep consistency between shopify and our database
    return (
      userSource
        .updateUser(userId, UserAddUpdateDto.userUpdateDto(userId, payload))
        .then((user: UserInfoDto) => {
          return storeSource.updateStore(
            customerId,
            StoreAddUpdateDto.fromUpdateDto(customerId, payload, user.id)
          );
        })
        // })
        .then(() => {
          return dispatch(updateCustomerSuccess());
        })
        .catch((e: object | null | string) => {
          return dispatch(updateCustomerFail(e));
        })
    );
  };

export const fetchStoreType = () => (dispatch: Dispatch<IActionType>) => {
  dispatch(getStoreTypeInit());
  return fetchStoreTypeApiCall()
    .then((storeTypes: Array<IAgentTitle>) =>
      dispatch(getStoreTypeSuccess(storeTypes))
    )
    .catch((e: object | null | string) => dispatch(getStoreTypeFail(e)));
};

export const fetchCustomerConfig = () => (dispatch: Dispatch<IActionType>) => {
  dispatch(fetchCustomerConfigInit());
  return fetchStoreConfigApiCall()
    .then((customerChannels: ICustomerConfigResponse) =>
      dispatch(fetchCustomerConfigSuccess(customerChannels))
    )
    .catch((e: object | null | string) => dispatch(fetchCustomerConfigFail(e)));
};

export const fetchCustomerZoneApiActions =
  (queryParams?: IGetZoneParams) => async (dispatch: Dispatch<IActionType>) => {
    dispatch(getCustomerZoneInit());
    return getCustomerZoneApiCall(queryParams)
      .then((response: any) => dispatch(getCustomerZoneSuccess(response)))
      .catch((error: any) => dispatch(getCustomerZoneFail(error)));
  };

export const createStore =
  (payload: IcreateUserPayload, id: string) =>
    (dispatch: Dispatch<IActionType>) => {
      dispatch(createCustomerInit());
      return storeSource
        .createStore(StoreAddUpdateDto.fromCreateDto(id, payload, id))
        .then(() => dispatch(createCustomerSuccess()))
        .catch((e: object | null | string) => dispatch(createCustomerFail(e)));
    };
